import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Upload, Trash2, Image as ImageIcon, Plus, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGallery = () => {
  const { adminAuth } = useOutletContext();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const [captionValue, setCaptionValue] = useState('');

  const fetchImages = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/gallery`);
      if (res.ok) setImages(await res.json());
    } catch (e) {
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const addImageRecord = async (url, caption) => {
    const res = await fetch(`${API_URL}/api/admin/gallery`, {
      method: 'POST',
      headers: { Authorization: `Basic ${adminAuth}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, caption: caption || '' }),
    });
    return res.ok;
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    let success = 0;
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await fetch(`${API_URL}/api/admin/upload/gallery`, {
          method: 'POST',
          headers: { Authorization: `Basic ${adminAuth}` },
          body: formData,
        });
        if (res.ok) {
          const data = await res.json();
          if (await addImageRecord(data.url, '')) success++;
        } else {
          const err = await res.json();
          toast.error(`Failed to upload ${file.name}: ${err.detail || ''}`);
        }
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (success > 0) {
      toast.success(`${success} image(s) added`);
      fetchImages();
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleAddUrl = async () => {
    if (!urlValue.trim()) {
      toast.error('Please enter an image URL');
      return;
    }
    if (await addImageRecord(urlValue.trim(), captionValue.trim())) {
      toast.success('Image added');
      setUrlValue('');
      setCaptionValue('');
      fetchImages();
    } else {
      toast.error('Failed to add image');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/gallery/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Basic ${adminAuth}` },
      });
      if (res.ok) {
        toast.success('Image deleted');
        fetchImages();
      } else {
        toast.error('Failed to delete');
      }
    } catch (e) {
      toast.error('Connection error');
    }
  };

  const getFullUrl = (url) => (!url ? '' : url.startsWith('http') ? url : `${API_URL}${url}`);

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gallery</h1>
          <p className="text-gray-600">Add and remove images shown on the public Gallery page</p>
        </div>
        <div className="relative">
          <input
            type="file"
            id="gallery-upload"
            multiple
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
            disabled={uploading}
          />
          <label htmlFor="gallery-upload">
            <Button asChild className="bg-amber-600 hover:bg-amber-700 cursor-pointer" disabled={uploading} data-testid="gallery-upload-btn">
              <span>
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload Images'}
              </span>
            </Button>
          </label>
        </div>
      </div>

      {/* Add by URL */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><LinkIcon className="w-4 h-4" /> Add image by URL</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              data-testid="gallery-url-input"
              value={urlValue}
              onChange={(e) => setUrlValue(e.target.value)}
              placeholder="https://image-url..."
              className="flex-1"
            />
            <Input
              data-testid="gallery-caption-input"
              value={captionValue}
              onChange={(e) => setCaptionValue(e.target.value)}
              placeholder="Caption (optional)"
              className="sm:w-64"
            />
            <Button onClick={handleAddUrl} data-testid="gallery-add-url-btn" className="bg-gray-800 hover:bg-gray-900">
              <Plus className="w-4 h-4 mr-2" /> Add
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Tip: You can also upload local images (max 10 MB each) using the "Upload Images" button.
          </p>
        </CardContent>
      </Card>

      {/* Images grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {images.map((image) => (
          <Card key={image.id} data-testid={`gallery-item-${image.id}`} className="overflow-hidden group relative">
            <div className="relative h-44">
              <img
                src={getFullUrl(image.url)}
                alt={image.caption || 'Gallery image'}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found'; }}
              />
              <button
                onClick={() => handleDelete(image.id)}
                data-testid={`gallery-delete-${image.id}`}
                className="absolute top-2 right-2 p-2 bg-red-500/90 rounded-full text-white shadow-md hover:bg-red-600 transition-colors"
                title="Delete image"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            {image.caption && (
              <CardContent className="p-3">
                <p className="text-sm text-gray-700 truncate">{image.caption}</p>
              </CardContent>
            )}
          </Card>
        ))}

        {images.length === 0 && (
          <div className="col-span-full">
            <Card className="border-dashed border-2">
              <CardContent className="p-12 text-center">
                <ImageIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No images yet</h3>
                <p className="text-gray-500">Upload images or add one by URL to get started.</p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {images.length > 0 && (
        <p className="text-sm text-gray-600">
          <strong>{images.length}</strong> image{images.length !== 1 ? 's' : ''} in the gallery.
        </p>
      )}
    </div>
  );
};

export default AdminGallery;
